import axios from "axios";
import { baseURL } from "../Constants";

const url = `${baseURL}/Tournament`;

export interface TournamentConfig {
    id: number;
    tournamentId: string;
    season: number;
    divisionName: string;
    displayName: string;
    isActive: boolean;
    countsTowardStats: boolean;
    qualifiedTeamIds: string | null;
    createdAt: string;
    updatedAt: string;
}

export interface QualifiedTeamsGroup {
    divisionName: string;
    displayName: string;
    teams: { teamId: string; teamName: string; teamLogo: string }[];
}

export interface TournamentTestResult {
    success: boolean;
    name?: string;
    teamCount?: number;
    totalMatches?: number;
    completedMatches?: number;
    matchesWithDemos?: number;
    teams?: { id: string; name: string; shortName: string; logo: string }[];
    message?: string;
}

export const getAllTournamentConfigs = async (): Promise<TournamentConfig[]> => {
    const response = await axios.get(url);
    return response.data;
};

export const getTournamentConfigsBySeason = async (season: number): Promise<TournamentConfig[]> => {
    const response = await axios.get(`${url}/season/${season}`);
    return response.data;
};

const authHeaders = (token: string) => ({
    headers: { Authorization: `Bearer ${token}` }
});

export const addTournamentConfig = async (
    config: Partial<TournamentConfig>,
    password: string,
    token: string
): Promise<TournamentConfig> => {
    const response = await axios.post(`${url}?password=${password}`, config, authHeaders(token));
    return response.data;
};

export const updateTournamentConfig = async (
    id: number,
    config: Partial<TournamentConfig>,
    password: string,
    token: string
): Promise<void> => {
    await axios.post(`${url}/update/${id}?password=${password}`, config, authHeaders(token));
};

export const deleteTournamentConfig = async (id: number, password: string, token: string): Promise<void> => {
    await axios.post(`${url}/delete/${id}?password=${password}`, {}, authHeaders(token));
};

export const getQualifiedTeams = async (season: number): Promise<QualifiedTeamsGroup[]> => {
    const response = await axios.get(`${url}/qualified/${season}`);
    return response.data;
};

export interface BracketTeam {
    teamId: string;
    name: string;
    logo: string;
}

export interface BracketMatch {
    matchId: string;
    /** Null means an empty slot, rendered as TBD. */
    team1: BracketTeam | null;
    team2: BracketTeam | null;
    /** Series score (maps won). For a best-of-one this is just 1-0. */
    score1: number;
    score2: number;
    /** Maps in the series: 1 = Bo1 (round score matters), 3 = Bo3 (map score matters). */
    mapCount: number;
    /**
     * Real round score (e.g. 13-6) for a best-of-one we have parsed, already oriented to
     * team1/team2. Null when unparsed or a multi-map series — fall back to score1/score2.
     */
    roundScore1: number | null;
    roundScore2: number | null;
    winnerId: string | null;
    isComplete: boolean;
    /**
     * Bracket progression contradicted the feed's winner, so the backend overrode it.
     * OverPass gets this wrong on some forfeits.
     */
    winnerDisputed: boolean;
    /**
     * Deduced from the bracket shape rather than reported by OverPass — the pairing and winner
     * are certain, the score is not. In practice these are forfeits the feed omitted.
     */
    isReconstructed: boolean;
    startDate: string | null;
}

export interface BracketRound {
    roundNumber: number;
    /**
     * Matches this round should hold per the bracket-size arithmetic, or 0 when that cannot be
     * known (a losers bracket has no such formula).
     */
    expectedMatchCount: number;
    matches: BracketMatch[];
}

/** "main" for single elimination; "winners" / "losers" / "grandFinal" for double. */
export type BracketSectionKey = 'main' | 'winners' | 'losers' | 'grandFinal';

export interface BracketSection {
    key: BracketSectionKey;
    rounds: BracketRound[];
}

export type BracketFormat = 'single-elimination' | 'double-elimination';

export interface QualifierBracket {
    divisionName: string;
    displayName: string;
    teamCount: number;
    bracketSize: number;
    byeCount: number;
    /**
     * Detected from the results, not configured. Season 32's qualifiers were single
     * elimination and season 33's are double, so both shapes have to render.
     */
    format: BracketFormat;
    sections: BracketSection[];
    /** Matches outside the elimination tree — seeding games between qualified teams. */
    seedingMatches: BracketMatch[];
    /** Non-fatal derivation problems, for admin visibility. */
    warnings: string[];
}

export const getQualifierBrackets = async (season: number): Promise<QualifierBracket[]> => {
    const response = await axios.get(`${url}/brackets/${season}`);
    return response.data;
};

export const testTournamentFetch = async (
    tournamentId: string,
    password: string,
    token: string
): Promise<TournamentTestResult> => {
    const response = await axios.get(`${url}/test/${tournamentId}?password=${password}`, authHeaders(token));
    return response.data;
};
